import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import RemoveWorkflowRunSource from '@Application/WorkflowRuns/Sources/Commands/RemoveWorkflowRunSource';
import removeWorkflowRunSource from '@Application/WorkflowRuns/Sources/removeWorkflowRunSource';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';

type Props = {
	readonly subject: SubjectAddress;
	readonly preparationId: number;
	readonly elementId: number;
	/** Title of the element, for the accessible name and the confirmation. */
	readonly elementTitle: string;
};

/**
 * « Retirer » on one element of an open preparation, behind a `ConfirmDialog`: the element only leaves
 * the preparation, the interview or document stays in the dossier. Leaf action component.
 */
const RemoveElementAction: React.FC<Props> = ({ subject, preparationId, elementId, elementTitle }) => {
	const dispatch = useDispatch();
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;
	const [isConfirmVisible, setIsConfirmVisible] = React.useState(false);
	const [isRemoving, setIsRemoving] = React.useState(false);

	const handleOpenConfirm = React.useCallback(() => {
		setIsConfirmVisible(true);
	}, []);

	const handleConfirmHide = React.useCallback(() => {
		setIsConfirmVisible(false);
	}, []);

	const handleConfirm = React.useCallback(() => {
		setIsRemoving(true);
		dispatch(
			removeWorkflowRunSource(
				new RemoveWorkflowRunSource(facilityId, subject, preparationId, elementId),
			),
		)
			.catch(ignoreHandledError)
			.finally(() => {
				setIsRemoving(false);
				setIsConfirmVisible(false);
			});
	}, [dispatch, facilityId, subject, preparationId, elementId]);

	return (
		<React.Fragment>
			<Button
				type="button"
				size="sm"
				severity="secondary"
				label="Retirer"
				onClick={handleOpenConfirm}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isConfirmVisible}
				onHide={handleConfirmHide}
				header="Retirer cet élément ?"
				message={`« ${elementTitle} » ne servira plus à générer les documents de cette préparation. Il reste dans le dossier.`}
				acceptLabel="Retirer"
				rejectLabel="Annuler"
				acceptSeverity="danger"
				defaultFocus="reject"
				loading={isRemoving}
				accept={handleConfirm}
			/>
		</React.Fragment>
	);
};

export default RemoveElementAction;
